import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { X, Rocket, Loader2, ShieldCheck, Info } from "lucide-react";
import { billingApi } from "../lib/api";
import EmbeddedStripeCheckoutModal from "./payments/EmbeddedStripeCheckoutModal";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Compact per-row "Activate user" modal. Lets the admin pick Monthly or Annual
 *  for a single teammate, then opens Stripe Embedded Checkout inline. Once the
 *  payment clears the parent Technicians page reloads via `activate_users_session`
 *  URL param polling. */
export default function ActivateOneUserModal({ user, status, onClose, onActivated }) {
  const [term, setTerm] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null); // {clientSecret, sessionId}

  const onComplete = useCallback(async () => {
    // Poll for confirmation, then let the parent refresh.
    const sid = checkout?.sessionId;
    if (!sid) { setCheckout(null); return; }
    for (let i = 0; i < 6; i += 1) {
      const s = await billingApi.status(sid).catch(() => null);
      if (s?.payment_status === "paid") {
        toast.success("Payment received — user activated!");
        setCheckout(null);
        onActivated?.();
        onClose?.();
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing. Refresh in a moment.");
    setCheckout(null);
  }, [checkout, onActivated, onClose]);

  if (!user) return null;

  const prorate = status?.prorate?.[term] || null;
  const otherProrate = status?.prorate?.[term === "monthly" ? "annual" : "monthly"] || null;
  const price = prorate?.perUserProrated || 0;
  const name = user.name || user.email;

  const submit = async () => {
    setBusy(true);
    try {
      const resp = await billingApi.activateUsers([user.id], term, window.location.origin, { embedded: true });
      if (resp?.client_secret) {
        setCheckout({ clientSecret: resp.client_secret, sessionId: resp.session_id });
      } else if (resp?.url) {
        // Fallback for old backends
        window.location.href = resp.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="activate-one-user-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-cyan-50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Rocket className="h-4 w-4 text-emerald-700" />
            </div>
            <div>
              <div className="text-[14px] font-bold text-slate-900">Activate user</div>
              <div className="text-[11.5px] text-slate-600 truncate max-w-[240px]">{name}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="activate-one-user-close"
            className="h-7 w-7 rounded hover:bg-white/60 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[11.5px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
              Billing term
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["monthly", "annual"].map((t) => {
                const p = status?.prorate?.[t];
                const on = term === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTerm(t)}
                    data-testid={`activate-one-term-${t}`}
                    className={`text-left rounded-xl border p-3 transition-colors ${
                      on
                        ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="text-[13px] font-semibold text-slate-900 capitalize">{t}</div>
                    <div className="text-[15px] font-bold text-slate-900 tabular-nums mt-0.5">
                      {money(p?.perUserProrated || 0)}
                    </div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">
                      {p?.anchorNew
                        ? `First ${t} activation`
                        : `${p?.daysLeft} day${p?.daysLeft === 1 ? "" : "s"} left`}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-1.5 text-[11.5px] text-slate-500">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              {prorate?.anchorNew
                ? `Starts your ${term} renewal date. Renews ${money(prorate?.perUserFull || 0)}/user per ${term === "annual" ? "year" : "month"}.`
                : `Prorated for ${prorate?.daysLeft} day${prorate?.daysLeft === 1 ? "" : "s"} left in the current ${term} cycle. Then ${money(prorate?.perUserFull || 0)}/user per ${term === "annual" ? "year" : "month"} at renewal.`}
              {" "}Switch to <button
                type="button"
                onClick={() => setTerm(term === "monthly" ? "annual" : "monthly")}
                className="underline font-semibold hover:text-slate-700"
              >
                {term === "monthly" ? "annual" : "monthly"}
              </button> · {money(otherProrate?.perUserProrated || 0)}.
            </span>
          </div>
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="text-[12px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400" /> Secure Stripe checkout
          </div>
          <button
            onClick={submit}
            disabled={busy || !price}
            data-testid="activate-one-user-submit"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[13.5px] font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Activate · {money(price)}
          </button>
        </div>
      </div>
    </div>
    {checkout && (
      <EmbeddedStripeCheckoutModal
        clientSecret={checkout.clientSecret}
        title={`Activate ${name}`}
        subtitle={`${term === "annual" ? "Annual" : "Monthly"} · ${money(price)}`}
        onComplete={onComplete}
        onClose={() => setCheckout(null)}
        testid="activate-one-embedded-checkout"
      />
    )}
    </>
  );
}
